import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assignLead, getUsers } from '@/services/api';
import { useAuth } from '@/services/auth';

interface Props {
  leadId: string;
  currentOwnerUserId: string | null;
}

export default function AssignOwnerLead({ leadId, currentOwnerUserId }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isEmployee = user?.role === 1;

  const { data: teamUsers } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
    enabled: !isEmployee,
  });

  // Employees can only assign to themselves; managers/admins get the full team list.
  const options = isEmployee && user?.userId
    ? [{ userId: user.userId, displayName: user.displayName, isActive: true }]
    : (teamUsers ?? []);

  const assign = useMutation({
    mutationFn: (newUserId: string | null) => assignLead(leadId, newUserId),
    onSuccess: () => {
      // T031: Invalidate lead detail + list views so they refetch after assignment
      qc.invalidateQueries({ queryKey: ['lead', leadId] });
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  return (
    <div className="flex items-center gap-2 text-sm">
      <label className="font-medium text-gray-600">Owner:</label>
      <select
        value={currentOwnerUserId ?? ''}
        onChange={e => assign.mutate(e.target.value || null)}
        disabled={assign.isPending}
        className="border rounded px-2 py-1 disabled:opacity-50"
      >
        <option value="">Unassigned</option>
        {options.map(u => (
          <option key={u.userId} value={u.userId}>
            {u.displayName}
            {!u.isActive && ' (Deactivated)'}
          </option>
        ))}
      </select>
    </div>
  );
}
